import { useSortable } from '@dnd-kit/sortable';
import { useEditorLanguages } from 'articleEditor/components/localePicker/useLocales';
import { RetranslateButton } from 'articleEditor/components/retranslateButton/retranslateButton';
import { RetranslateLoading } from 'articleEditor/components/retranslateButton/retranslateLoading';
import { RetranslationContext } from 'articleEditor/contexts/retranslationProvider';
import { translatingStatusAtom } from 'articleEditor/hooks/translations/useBaseTranslation';
import { useTranslateBlock } from 'articleEditor/hooks/translations/useTranslateBlock';
import { MenuOption } from 'articleEditor/utils/actions';
import { toPx } from 'articleEditor/utils/css';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import {
  findNodePath,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { MyRootBlock } from 'lib-knowledge-document-structure/plateTypes';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { featureFlagsAtom } from 'lib-knowledge-utils/stores/featureFlags';
import { ampli } from 'lib-shared-tracking';
import { useContext, useMemo, useRef, useState } from 'react';

import s from './block.module.scss';
import { BlockActions } from './blockActions';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  element: MyRootBlock;
  blockActions: ReadonlyArray<MenuOption>;
};

const SortableBlockWrapper = ({
  element,
  className,
  blockActions,
  children,
  ...props
}: Props): JSX.Element => {
  const { readOnly } = useEditableProps();
  const [showingMenu, setShowingMenu] = useState(false);
  const { defaultLanguage, selectedLanguage } = useEditorLanguages();
  const translateBlock = useTranslateBlock();
  const nodesThatNeedReTranslation = useContext(RetranslationContext);
  const { featureFlags } = useAtomValue(featureFlagsAtom);
  const translatingStatus = useAtomValue(translatingStatusAtom);
  const sortable = useSortable({
    id: element.id,
    transition: {
      // Faster than the 200ms it takes for the BlockActions to appear,
      // so they don't appear while you drag.
      duration: 150,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });
  const ref = useRef<HTMLDivElement>(null);
  const isTranslatingBlock =
    translatingStatus.type == TranslatingType.BLOCK &&
    translatingStatus.blockId == element.id;

  const editor = useMyEditorRef();
  const path = findNodePath(editor, element);

  const retranslateLoading = useMemo(() => {
    if (!isTranslatingBlock) {
      return <></>;
    }
    return <RetranslateLoading />;
  }, [isTranslatingBlock]);

  if (path && path.length > 1) {
    return <>{children}</>;
  }

  if (readOnly) {
    return (
      <div className={cx(s.block, s.blockDisabled, className)} ref={ref}>
        {children}
        {retranslateLoading}
      </div>
    );
  }

  const style = {
    '--translate-y': toPx(sortable.transform?.y || 0),
    transition: sortable.transition,
  };

  const translationTooltip = `The content of this block has been modified in the default language since it was last translated. Click here to translate it again.`;
  return (
    <div
      className={cx(
        s['block'],
        className,
        sortable.isDragging && s['isDragging'],
        showingMenu && s['blockMenuShowing'],
      )}
      ref={sortable.setNodeRef}
      style={style}
      {...props}
      {...sortable.attributes}
    >
      {featureFlags.aiAutoTranslation &&
      defaultLanguage.languageId !== selectedLanguage.languageId &&
      nodesThatNeedReTranslation.has(element.id) ? (
        <RetranslateButton
          translationTooltip={translationTooltip}
          translateFunction={() => {
            ampli.knowledgeRetranslateArticleBlockClicked({
              languageId: selectedLanguage.languageId,
            });
            translateBlock(element.id);
          }}
        />
      ) : (
        <></>
      )}
      {retranslateLoading}
      <BlockActions
        element={element}
        blockActions={blockActions}
        showingMenu={showingMenu}
        setShowingMenu={setShowingMenu}
        sortable={sortable}
      />
      {children}
    </div>
  );
};

export default SortableBlockWrapper;
